import { useState, useEffect, useRef, useMemo, useCallback } from "react";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { renderAlarm, renderWarning } from "../../components/StatusChip";
import {getEventStatus} from "../../api/eventStatusAPI";
import {getProjects} from "../../api/projectAPI";
import { getNode } from "../../api/nodeAPI";
import { useObjectData } from "../../hooks/useLocalStorageData";
import { useRefreshInterval as useRefreshIntervalHook } from "../../hooks/useInterval";
import { useObjectMaps } from "../../hooks/useObjectMaps";

const StyledText = styled("text", {
  shouldForwardProp: (prop) => prop !== "variant",
})(({ theme }) => ({
  textAnchor: "middle",
  dominantBaseline: "central",
  fill: (theme.vars || theme).palette.text.secondary,
  variants: [
    {
      props: { variant: "primary" },
      style: {
        fontSize: theme.typography.h5.fontSize,
        fontWeight: theme.typography.h5.fontWeight,
      },
    },
    {
      props: ({ variant }) => variant !== "primary",
      style: {
        fontSize: theme.typography.body2.fontSize,
        fontWeight: theme.typography.body2.fontWeight,
      },
    },
  ],
}));

function PieCenterLabel({ primaryText, secondaryText }) {
  const { width, height, left, top } = useDrawingArea();
  const primaryY = top + height / 2 - 10;
  const secondaryY = primaryY + 24;
  return (
    <>
      <StyledText variant="primary" x={left + width / 2} y={primaryY}>
        {primaryText}
      </StyledText>
      <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
        {secondaryText}
      </StyledText>
    </>
  );
}

export default function MiniDataGrid() {
  const [rows, setRows] = useState([]);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogNodes, setDialogNodes] = useState([]);
  const [dialogAlarmType, setDialogAlarmType] = useState(2);
  const [dialogStatusDist, setDialogStatusDist] = useState([]);
  const [dialogEpochData, setDialogEpochData] = useState({ labels: [], data: [] });
  const inFlightRef = useRef(false);
  const rawDataRef = useRef([]);
  const nodeMapRef = useRef(new Map());
  const objectData = useObjectData();

  // Use centralized object maps hook
  const { nameMap: objectNameMap, descMap: objectDescMap } = useObjectMaps(objectData);

  const fetchData = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setError(null);
    setLoading(true);
    try {
      const projectCode = localStorage.getItem("projectCode");
      const projectData = await getProjects();
      const currentProject = projectData.find(
        (project) => project.code === projectCode
      );
      if (!currentProject || !currentProject.projectDashboardJSon) {
        setLoading(false);
        inFlightRef.current = false;
        return;
      }
      const projectEvents = JSON.parse(currentProject.projectDashboardJSon);
      const [fullList, allNodes] = await Promise.all([
        getEventStatus(),
        getNode(),
      ]);
      nodeMapRef.current = new Map(allNodes.map((n) => [n.idNode, n]));
      const filteredList = fullList.filter((item) =>
        projectEvents.includes(item.event)
      );
      rawDataRef.current = filteredList;
      const eventCounts = {};
      filteredList.forEach((item) => {
        const eventId = item.event;
        const alarmType = item.alarmType;
        if (!eventCounts[eventId]) {
          eventCounts[eventId] = {
            event: objectNameMap.get(eventId.toString()) || eventId,
            warning: 0,
            alarm: 0,
          };
        }
        if (alarmType === 1) {
          eventCounts[eventId].warning += 1;
        } else if (alarmType === 2) {
          eventCounts[eventId].alarm += 1;
        }
      });
      projectEvents.forEach((eventVal) => {
        if (!eventCounts[eventVal]) {
          eventCounts[eventVal] = {
            event: objectNameMap.get(eventVal.toString()) || eventVal,
            warning: 0,
            alarm: 0,
          };
        }
      });
      const rowsData = Object.keys(eventCounts).map((key) => ({
        id: key,
        eventName: eventCounts[key].event,
        error: eventCounts[key].alarm,
        warning: eventCounts[key].warning,
      }));
      setRows(rowsData);
    } catch (err) {
      setError(err.message || "Veri alınamadı");
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  }, [objectNameMap]);

  // Use refresh interval hook (handles interval changes and refresh all events)
  useRefreshIntervalHook(fetchData, {
    defaultInterval: 60,
    storageKey: 'refreshInterval'
  });

  const handleCountClick = useCallback((eventId, alarmType) => {
    const items = rawDataRef.current.filter(
      (item) => String(item.event) === String(eventId) && item.alarmType === alarmType
    );
    const nodeCountMap = new Map();
    items.forEach((item) => {
      const existing = nodeCountMap.get(item.nodeId);
      if (existing) {
        existing.count += 1;
      } else {
        const node = nodeMapRef.current.get(item.nodeId);
        nodeCountMap.set(item.nodeId, {
          name: node?.name || `Node ${item.nodeId}`,
          idNode: item.nodeId,
          count: 1,
        });
      }
    });
    const groupedNodes = Array.from(nodeCountMap.values())
      .sort((a, b) => b.count - a.count);

    // Status distribution for donut (normal vs warning vs alarm)
    const allEventItems = rawDataRef.current.filter(
      (item) => String(item.event) === String(eventId)
    );
    const normalCount = allEventItems.filter((i) => i.alarmType === 0).length;
    const warningCount = allEventItems.filter((i) => i.alarmType === 1).length;
    const alarmCount = allEventItems.filter((i) => i.alarmType === 2).length;
    setDialogStatusDist(
      [
        { id: 0, label: t("dashboard.topicHeader.normal"), value: normalCount, color: "#4caf50" },
        { id: 1, label: t("places.dataGrid.warning"), value: warningCount, color: "#f57c00" },
        { id: 2, label: t("places.dataGrid.error"), value: alarmCount, color: "#c62828" },
      ].filter((d) => d.value > 0)
    );

    // Epoch-based timeline
    const epochItems = items
      .filter((i) => i.epoch != null)
      .sort((a, b) => a.epoch - b.epoch);
    const dayMap = new Map();
    epochItems.forEach((item) => {
      let epoch = Number(item.epoch);
      if (epoch < 1e12) epoch *= 1000;
      const date = new Date(epoch);
      const dayKey = `${date.getDate().toString().padStart(2, "0")}.${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}`;
      dayMap.set(dayKey, (dayMap.get(dayKey) || 0) + 1);
    });
    setDialogEpochData({
      labels: Array.from(dayMap.keys()),
      data: Array.from(dayMap.values()),
    });

    const row = rows.find((r) => String(r.id) === String(eventId));
    const eventName = row?.eventName || eventId;
    const typeLabel = alarmType === 1 ? t("places.dataGrid.warning") : t("places.dataGrid.error");
    setDialogTitle(`${eventName} - ${typeLabel}`);
    setDialogNodes(groupedNodes);
    setDialogAlarmType(alarmType);
    setDialogOpen(true);
  }, [rows, t]);

  const columns = [
    {
      field: "eventName",
      headerName: t("alarmHistory.eventName"),
      width: 200,
      flex: 2,
      renderHeader: () => null,
      renderCell: (params) => {
        const idKey = params.row?.id != null ? params.row.id.toString() : "";
        const tooltipText = objectDescMap.get(idKey) ?? "-";
        return (
          <Tooltip
            arrow
            disableInteractive
            title={
              <Box sx={{ maxWidth: 520 }}>
                <Box
                  sx={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.5 }}
                >
                  {tooltipText}
                </Box>
              </Box>
            }
            componentsProps={{
              tooltip: {
                sx: (theme) => ({
                  maxWidth: 560,
                  p: 1.5,
                  fontSize: 14,
                  bgcolor:
                    theme.palette.mode === "dark" ? "#ffffff" : "#000000",
                  color: theme.palette.mode === "dark" ? "#000000" : "#ffffff",
                  boxShadow: 4,
                  borderRadius: 1,
                  border: `1px solid ${
                    theme.palette.mode === "dark" ? "#e5e7eb" : "#333333"
                  }`,
                }),
              },
              arrow: {
                sx: (theme) => ({
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#000000",
                }),
              },
            }}
          >
            <Box
              component="span"
              sx={{
                transition: "color 0.15s ease",
                "&:hover": (theme) => ({
                  textDecoration: "underline",
                  textDecorationColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  cursor: "pointer",
                }),
              }}
              onClick={() => {
                const row = params.row;
                const hasIssues = row.error > 0 || row.warning > 0;
                navigate(hasIssues ? `/vending-machines?event=${row.id}` : "/vending-machines");
              }}
            >
              {params.value}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "error",
      headerName: t("places.dataGrid.error"),
      flex: 1,
      minWidth: 60,
      renderCell: (params) => (
        <Box
          onClick={(e) => {
            if (params.value > 0) {
              e.stopPropagation();
              handleCountClick(params.row.id, 2);
            }
          }}
          sx={{ cursor: params.value > 0 ? "pointer" : "default", width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          {renderAlarm(params.value)}
        </Box>
      ),
    },
    {
      field: "warning",
      headerName: t("places.dataGrid.warning"),
      flex: 1,
      minWidth: 60,
      renderCell: (params) => (
        <Box
          onClick={(e) => {
            if (params.value > 0) {
              e.stopPropagation();
              handleCountClick(params.row.id, 1);
            }
          }}
          sx={{ cursor: params.value > 0 ? "pointer" : "default", width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          {renderWarning(params.value)}
        </Box>
      ),
    },
  ];

  return (
    <div style={{ position: "relative" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        rowHeight={44}
        headerHeight={48}
        density="standard"
        hideFooter
        loading={loading}
        sx={{
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            display: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          },
          cursor: "pointer",
          borderRadius: 0,
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-columnSeparator": {
            visibility: "hidden",
          },
        }}
      />
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          {dialogTitle}
          <IconButton onClick={() => setDialogOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          {dialogNodes.length > 0 && (
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", justifyContent: "center" }}>
              {/* Donut Chart - Status Distribution */}
              {dialogStatusDist.length > 0 && (
                <Box sx={{ flex: "0 0 auto" }}>
                  <PieChart
                    margin={{ left: 10, right: 10, top: 10, bottom: 10 }}
                    series={[
                      {
                        data: dialogStatusDist,
                        innerRadius: 50,
                        outerRadius: 75,
                        paddingAngle: 1,
                        highlightScope: { faded: "global", highlighted: "item" },
                      },
                    ]}
                    height={180}
                    width={180}
                    slotProps={{ legend: { hidden: true } }}
                  >
                    <PieCenterLabel
                      primaryText={String(dialogStatusDist.reduce((sum, d) => sum + d.value, 0))}
                      secondaryText={t("dashboard.topicHeader.total") || "Toplam"}
                    />
                  </PieChart>
                </Box>
              )}
              {/* Epoch Timeline Chart */}
              {dialogEpochData.labels.length > 0 && (
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <BarChart
                    xAxis={[
                      {
                        scaleType: "band",
                        data: dialogEpochData.labels,
                        tickLabelStyle: { fontSize: 11, angle: -45, textAnchor: "end" },
                      },
                    ]}
                    series={[
                      {
                        data: dialogEpochData.data,
                        color: dialogAlarmType === 1 ? "#f57c00" : "#c62828",
                      },
                    ]}
                    height={180}
                    margin={{ left: 40, right: 10, top: 10, bottom: 50 }}
                    slotProps={{ legend: { hidden: true } }}
                  />
                </Box>
              )}
            </Box>
          )}
          <List dense disablePadding>
            {dialogNodes.map((node, idx) => (
              <ListItemButton
                key={idx}
                onClick={() => navigate(`/places/${node.idNode}`)}
                sx={{ py: 1 }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: dialogAlarmType === 2 ? "#c62828" : "#f57c00",
                    mr: 1.5,
                    flexShrink: 0,
                  }}
                />
                <ListItemText
                  primary={node.name}
                  secondary={`(${node.count})`}
                  secondaryTypographyProps={{ component: "span", sx: { ml: 1 } }}
                  sx={{ display: "flex", alignItems: "center" }}
                  primaryTypographyProps={{ component: "span" }}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      {error && !loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
            color: "#b00020",
            fontWeight: 500,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
